import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchRental, updateRental, createAgreement, sendAgreement, createInspection, fetchActivity, createActivityNote, fetchRentalInvoices, createInvoice, sendInvoiceEmail, updateInvoiceStatus, downloadInvoicePdf, downloadAgreementPdf, getUploadUrl } from "@/lib/api";
import { RENTAL_STATUS_LABELS, RENTAL_STATUS_COLORS, FUEL_LEVELS, formatDateTime, VEHICLE_TYPE_LABELS, INVOICE_STATUS_COLORS, RENT_MODE_LABELS, TIME_PERIOD_LABELS } from "@/lib/constants";
import { getErrorMessage } from "@/lib/utils";
import { CaretLeft, PaperPlaneTilt, CheckCircle, Clipboard, WarningCircle, ArrowCounterClockwise, XCircle, FilePdf, Envelope, CurrencyDollar, Plus, Receipt, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhotoUpload } from "@/components/PhotoUpload";

const getBillingDays = (rental) => {
  if (rental?.rent_mode === "PAYG" || rental?.time_period === "WEEKLY") return 7;
  if (rental?.time_period === "DAILY") return 1;
  return 7;
};

const getRateSummary = (rental) => {
  if (rental?.rent_mode === "PAYG" || rental?.time_period === "WEEKLY") {
    return `$${Number(rental?.weekly_rate || 0).toFixed(2)}/week`;
  }
  return `$${Number(rental?.daily_rate || 0).toFixed(2)}/day`;
};

const getBillingLabel = (rental) => {
  if (rental?.rent_mode === "PAYG") return "Weekly until the vehicle is returned";
  return TIME_PERIOD_LABELS[rental?.time_period] || rental?.time_period || "Weekly";
};

export default function RentalDetailPage({ users, currentUser }) {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const [rental, setRental] = useState(null);
  const [activity, setActivity] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [showInspection, setShowInspection] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [invoicePeriod, setInvoicePeriod] = useState({ period_start: "", period_end: "" });
  const [inspection, setInspection] = useState({ odometer: 0, fuel_level: "FULL", existing_damage: false, damage_notes: "", new_damage: false, missing_items: false, missing_items_notes: "", notes: "", photos: [] });

  const loadRental = useCallback(async () => {
    try {
      const [r, a, inv] = await Promise.all([fetchRental(rentalId), fetchActivity(rentalId), fetchRentalInvoices(rentalId)]);
      setRental(r);
      setActivity(a);
      setInvoices(inv);
    } catch { toast.error("Failed to load rental"); }
    finally { setLoading(false); }
  }, [rentalId]);

  useEffect(() => { loadRental(); }, [loadRental]);

  const getUserName = (id) => users?.find(u => u.id === id)?.full_name || "System";

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === "send_agreement") {
        let agr = rental.agreement;
        if (!agr) agr = await createAgreement(rentalId);
        const res = await sendAgreement(agr.id);
        if (res.email_sent) {
          toast.success(res.message || "Agreement emailed");
        } else {
          const url = window.location.origin + res.signing_url;
          await navigator.clipboard.writeText(url);
          toast.success(res.message || "Signing link copied to clipboard");
        }
        (res.delivery_warnings || []).forEach((warning) => toast.warning(warning));
      } else if (action === "close") {
        await updateRental(rentalId, { status: "CLOSED" });
        toast.success("Rental closed");
      } else if (action === "cancel") {
        await updateRental(rentalId, { status: "CANCELLED" });
        toast.success("Rental cancelled");
      } else if (action === "reactivate") {
        await updateRental(rentalId, { status: "DRAFT" });
        toast.success("Rental reactivated");
      }
      await loadRental();
    } catch (err) { toast.error(getErrorMessage(err, "Action failed")); }
    finally { setActionLoading(""); }
  };

  const handleInspection = async (type) => {
    setActionLoading("inspection");
    try {
      await createInspection(rentalId, { ...inspection, type });
      toast.success(`${type} inspection completed`);
      setShowInspection(null);
      await loadRental();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setActionLoading(""); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await createActivityNote(rentalId, note);
      setNote("");
      const a = await fetchActivity(rentalId);
      setActivity(a);
    } catch { toast.error("Failed to add note"); }
  };

  const handleCreateInvoice = async () => {
    if (!invoicePeriod.period_start || !invoicePeriod.period_end) { toast.error("Select period dates"); return; }
    setActionLoading("create_invoice");
    try {
      await createInvoice(rentalId, invoicePeriod);
      toast.success("Invoice created");
      setShowNewInvoice(false);
      setInvoicePeriod({ period_start: "", period_end: "" });
      await loadRental();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to create invoice")); }
    finally { setActionLoading(""); }
  };

  const handleSendInvoice = async (invoiceId) => {
    setActionLoading(`send_${invoiceId}`);
    try {
      const res = await sendInvoiceEmail(invoiceId);
      toast.success(res.message || "Invoice sent");
      await loadRental();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to send invoice")); }
    finally { setActionLoading(""); }
  };

  const handleMarkPaid = async (invoiceId) => {
    setActionLoading(`paid_${invoiceId}`);
    try {
      await updateInvoiceStatus(invoiceId, { status: "PAID" });
      toast.success("Invoice marked as paid");
      await loadRental();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to update")); }
    finally { setActionLoading(""); }
  };

  const suggestNextPeriod = () => {
    const days = getBillingDays(rental);
    let start;
    if (invoices.length > 0) {
      const lastEnd = new Date(invoices[0].period_end);
      start = new Date(lastEnd);
      start.setDate(start.getDate() + 1);
    } else {
      start = rental?.pickup_datetime ? new Date(rental.pickup_datetime) : new Date();
    }
    const end = new Date(start);
    end.setDate(end.getDate() + days - 1);
    setInvoicePeriod({
      period_start: start.toISOString().slice(0, 16),
      period_end: end.toISOString().slice(0, 16),
    });
    setShowNewInvoice(true);
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-sm text-slate-400">Loading...</div></div>;
  if (!rental) return <div className="flex items-center justify-center h-full"><div className="text-sm text-red-500">Rental not found</div></div>;

  const sc = RENTAL_STATUS_COLORS[rental.status] || {};
  const c = rental.customer || {};
  const v = rental.vehicle || {};
  const agr = rental.agreement;
  const hasCustomerDetails = Boolean(rental.customer?.id);
  const pickupInsp = rental.inspections?.find(i => i.type === "PICKUP");
  const returnInsp = rental.inspections?.find(i => i.type === "RETURN");
  const nextStepConfig = {
    DRAFT: {
      title: "Generate and send the agreement",
      description: "This will create the agreement if needed, email it when configured, otherwise copy the signing link, and move the rental into pending signature.",
      tone: "bg-foreground/5 border-black/10 text-foreground",
    },
    PENDING_SIGNATURE: {
      title: "Waiting for the customer to sign",
      description: "Keep the signing link handy so you can re-share it quickly. Once signed, the rental becomes ready for pickup.",
      tone: "bg-amber-50 border-amber-200 text-amber-900",
    },
    READY_FOR_PICKUP: {
      title: "Complete pickup inspection next",
      description: "Record the pickup condition, odometer, fuel level, and photos before handing over the vehicle.",
      tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
    },
    ON_HIRE: {
      title: "Manage the active hire",
      description: "Create invoices for each billing period and complete the return inspection when the vehicle comes back.",
      tone: "bg-sky-50 border-sky-200 text-sky-900",
    },
    RETURNED: {
      title: "Review the return and close the rental",
      description: "If everything is resolved, close the rental. If damage needs follow-up, move it through damage review first.",
      tone: "bg-violet-50 border-violet-200 text-violet-900",
    },
    DAMAGE_REVIEW: {
      title: "Resolve outstanding damage items",
      description: "Once damage review is complete, close the rental so it no longer appears in active workflows.",
      tone: "bg-rose-50 border-rose-200 text-rose-900",
    },
    CANCELLED: {
      title: "Rental has been cancelled",
      description: "Reactivate it if the booking is proceeding again.",
      tone: "bg-slate-100 border-slate-200 text-slate-700",
    },
    CLOSED: {
      title: "Rental is complete",
      description: "Use the activity log and invoices below if you need to review the completed hire.",
      tone: "bg-teal-50 border-teal-200 text-teal-900",
    },
  }[rental.status];

  const actions = [];
  if (rental.status === "DRAFT") actions.push({ key: "send_agreement", label: "Generate & Send Agreement", icon: PaperPlaneTilt, color: "bg-black hover:bg-primary/90 text-white" });
  if (rental.status === "READY_FOR_PICKUP" && !pickupInsp) actions.push({ key: "pickup", label: "Pickup Inspection", icon: CheckCircle, color: "bg-emerald-600 hover:bg-emerald-700 text-white" });
  if (rental.status === "ON_HIRE" && !returnInsp) actions.push({ key: "return", label: "Return Inspection", icon: ArrowCounterClockwise, color: "bg-purple-600 hover:bg-purple-700 text-white" });
  if (["RETURNED", "DAMAGE_REVIEW"].includes(rental.status)) actions.push({ key: "close", label: "Close Rental", icon: CheckCircle, color: "bg-teal-600 hover:bg-teal-700 text-white" });
  if (["DRAFT", "PENDING_SIGNATURE", "READY_FOR_PICKUP"].includes(rental.status)) actions.push({ key: "cancel", label: "Cancel", icon: XCircle, color: "bg-white border border-red-300 text-red-600 hover:bg-red-50" });
  if (rental.status === "CANCELLED") actions.push({ key: "reactivate", label: "Reactivate", icon: ArrowCounterClockwise, color: "bg-slate-700 hover:bg-slate-800 text-white" });

  return (
    <div className="h-full overflow-y-auto" data-testid="rental-detail-page">
      <div className="max-w-5xl mx-auto p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button data-testid="rental-back" onClick={() => navigate("/rentals")} className="w-9 h-9 border border-slate-200 rounded-md flex items-center justify-center hover:bg-slate-50">
              <CaretLeft weight="bold" className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-black tracking-tight font-heading text-slate-900">{rental.rental_number}</h1>
                <span className="text-xs font-bold uppercase px-2.5 py-1 rounded-md border" style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                  {RENTAL_STATUS_LABELS[rental.status]}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {(c.full_name || rental.customer_email || "Customer pending")}&middot; {v.make} {v.model} ({v.registration})
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {actions.map(a => {
              const Icon = a.icon;
              return (
                <button key={a.key} data-testid={`action-${a.key}`}
                  onClick={() => a.key === "pickup" || a.key === "return" ? setShowInspection(a.key === "pickup" ? "PICKUP" : "RETURN") : handleAction(a.key)}
                  disabled={actionLoading === a.key}
                  className={`flex items-center gap-2 h-10 px-4 rounded-md text-sm font-bold transition-colors ${a.color}`}>
                  <Icon weight="bold" className="w-4 h-4" /> {actionLoading === a.key ? "..." : a.label}
                </button>
              );
            })}
          </div>
        </div>

        {nextStepConfig && (
          <div data-testid="rental-next-step" className={`border rounded-md px-4 py-3 ${nextStepConfig.tone}`}>
            <p className="text-xs font-bold uppercase tracking-wider mb-1">Next Step</p>
            <p className="font-semibold">{nextStepConfig.title}</p>
            <p className="text-sm mt-1 opacity-90">{nextStepConfig.description}</p>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="customer-panel">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Customer</h3>
            <div className="space-y-1.5 text-sm">
              {hasCustomerDetails ? (
                <>
                  <div className="font-bold text-slate-900">{c.full_name}</div>
                  <div className="text-slate-600">{c.mobile} {c.email ? `| ${c.email}` : ""}</div>
                  {c.address && <div className="text-slate-500">{c.address}, {c.suburb} {c.state} {c.postcode}</div>}
                  {c.licence_number && <div className="text-slate-500">Licence: {c.licence_number} ({c.licence_state}) exp: {c.licence_expiry}</div>}
                  {c.licence_photo_id && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <a href={getUploadUrl(c.licence_photo_id)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground hover:underline" data-testid="view-licence-photo">
                        <IdentificationCard weight="bold" className="w-4 h-4" /> View Licence Photo
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2" data-testid="customer-pending-state">
                  <div className="inline-flex items-center rounded-md bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 border border-amber-200">
                    Customer details pending
                  </div>
                  <div className="font-semibold text-slate-900">{rental.customer_email || "No customer email recorded"}</div>
                  <div className="text-slate-500">
                    The customer has not completed their personal details, licence upload, and signature yet.
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Vehicle */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Vehicle</h3>
            <div className="space-y-1.5 text-sm">
              <div className="font-bold text-slate-900">{v.make} {v.model} ({v.year})</div>
              <div className="text-slate-600">{v.registration} &middot; {v.color} &middot; {VEHICLE_TYPE_LABELS[v.type]}</div>
              <div className="text-slate-500">{v.fuel_type} &middot; {v.odometer?.toLocaleString()} km &middot; {v.branch}</div>
            </div>
          </div>
          {/* Terms */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Rental Terms</h3>
            <div className="space-y-1.5 text-sm">
              <div><span className="text-slate-500">Pickup:</span> <span className="font-semibold">{formatDateTime(rental.pickup_datetime)}</span></div>
              <div><span className="text-slate-500">Planned Return:</span> <span className="font-semibold">{rental.return_datetime ? formatDateTime(rental.return_datetime) : "Open-ended"}</span></div>
              <div><span className="text-slate-500">Rent Mode:</span> <span className="font-semibold">{RENT_MODE_LABELS[rental.rent_mode] || rental.rent_mode || "Fixed Term"}</span></div>
              <div><span className="text-slate-500">Billing Period:</span> <span className="font-semibold">{getBillingLabel(rental)}</span></div>
              <div><span className="text-slate-500">Rate:</span> <span className="font-bold text-foreground">{getRateSummary(rental)}</span></div>
              <div><span className="text-slate-500">Bond:</span> <span className="font-bold">${rental.bond_amount}</span></div>
              {rental.notes && <div className="text-slate-500 mt-2 pt-2 border-t border-slate-100">Notes: {rental.notes}</div>}
            </div>
          </div>
          {/* Agreement */}
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Agreement</h3>
            {agr ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-md ${agr.status === "SIGNED" ? "bg-emerald-100 text-emerald-700" : agr.status === "SENT" ? "bg-amber-100 text-amber-700" : agr.status === "VIEWED" ? "bg-foreground/5 text-foreground/70" : "bg-slate-100 text-slate-600"}`}>
                    {agr.status}
                  </span>
                  {agr.signed_at && <span className="text-xs text-slate-400">Signed {formatDateTime(agr.signed_at)}</span>}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {agr.signing_token && (
                    <button data-testid="copy-signing-link" onClick={() => { navigator.clipboard.writeText(window.location.origin + "/sign/" + agr.signing_token); toast.success("Link copied"); }}
                      className="flex items-center gap-1.5 text-xs text-foreground font-bold hover:underline">
                      <Clipboard weight="bold" className="w-3.5 h-3.5" /> Copy Link
                    </button>
                  )}
                  {agr.signing_token && (
                    <button
                      data-testid="open-signing-page"
                      onClick={() => window.open(`${window.location.origin}/sign/${agr.signing_token}`, "_blank", "noopener,noreferrer")}
                      className="flex items-center gap-1.5 text-xs text-slate-700 font-bold hover:underline"
                    >
                      <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5" /> Open Signing Page
                    </button>
                  )}
                  <button
                    data-testid="download-agreement-pdf"
                    onClick={() => downloadAgreementPdf(agr.id, `Agreement-${rental.rental_number}.pdf`)}
                    className="flex items-center gap-1.5 text-xs text-red-600 font-bold hover:underline"
                  >
                    <FilePdf weight="bold" className="w-3.5 h-3.5" /> Download PDF
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No agreement created yet</p>
            )}
          </div>
        </div>

        {/* Inspections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[{ type: "PICKUP", data: pickupInsp, label: "Pickup Inspection" }, { type: "RETURN", data: returnInsp, label: "Return Inspection" }].map(({ type, data: insp, label }) => (
            <div key={type} className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{label}</h3>
              {insp ? (
                <div className="space-y-1.5 text-sm">
                  <div><span className="text-slate-500">Odometer:</span> <span className="font-semibold">{insp.odometer?.toLocaleString()} km</span></div>
                  <div><span className="text-slate-500">Fuel:</span> <span className="font-semibold">{insp.fuel_level}</span></div>
                  {insp.existing_damage && <div className="text-amber-600 font-semibold flex items-center gap-1"><WarningCircle weight="bold" className="w-4 h-4" /> Existing damage noted</div>}
                  {insp.new_damage && <div className="text-red-600 font-semibold flex items-center gap-1"><WarningCircle weight="bold" className="w-4 h-4" /> New damage found</div>}
                  {insp.missing_items && <div className="text-red-600 font-semibold">Missing items reported</div>}
                  {insp.notes && <div className="text-slate-500">{insp.notes}</div>}
                  {insp.photos && insp.photos.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Photos ({insp.photos.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {insp.photos.map((pid, idx) => (
                          <a key={pid} href={getUploadUrl(pid)} target="_blank" rel="noopener noreferrer" className="block w-16 h-16 rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-black/10 transition-shadow">
                            <img src={getUploadUrl(pid)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-slate-400 pt-1">Completed {formatDateTime(insp.completed_at)}</div>
                </div>
              ) : <p className="text-sm text-slate-400">Not completed</p>}
            </div>
          ))}
        </div>

        {/* Inspection Form Modal */}
        {showInspection && (
          <div className="bg-white border-2 border-black rounded-md p-6 space-y-4">
            <h3 className="text-lg font-bold font-heading text-slate-900">{showInspection} Inspection</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Odometer (km)</label>
                <input data-testid="inspection-odometer" type="number" className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10" value={inspection.odometer} onChange={e => setInspection(p => ({...p, odometer: parseInt(e.target.value) || 0}))} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Fuel Level</label>
                <select data-testid="inspection-fuel" className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md bg-white" value={inspection.fuel_level} onChange={e => setInspection(p => ({...p, fuel_level: e.target.value}))}>
                  {FUEL_LEVELS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            {showInspection === "PICKUP" && (
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input data-testid="inspection-existing-damage" type="checkbox" className="rounded" checked={inspection.existing_damage} onChange={e => setInspection(p => ({...p, existing_damage: e.target.checked}))} />
                Existing damage present
              </label>
            )}
            {showInspection === "RETURN" && (
              <>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input data-testid="inspection-new-damage" type="checkbox" className="rounded" checked={inspection.new_damage} onChange={e => setInspection(p => ({...p, new_damage: e.target.checked}))} />
                  New damage found
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input data-testid="inspection-missing-items" type="checkbox" className="rounded" checked={inspection.missing_items} onChange={e => setInspection(p => ({...p, missing_items: e.target.checked}))} />
                  Missing items
                </label>
              </>
            )}
            {(inspection.existing_damage || inspection.new_damage) && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Damage Notes</label>
                <textarea data-testid="inspection-damage-notes" rows={2} className="w-full px-4 py-3 text-sm border border-slate-200 rounded-md resize-none" value={inspection.damage_notes} onChange={e => setInspection(p => ({...p, damage_notes: e.target.value}))} />
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Notes</label>
              <textarea data-testid="inspection-notes" rows={2} className="w-full px-4 py-3 text-sm border border-slate-200 rounded-md resize-none" value={inspection.notes} onChange={e => setInspection(p => ({...p, notes: e.target.value}))} />
            </div>
            <PhotoUpload
              fileIds={inspection.photos}
              onChange={(ids) => setInspection(p => ({...p, photos: ids}))}
              max={10}
              label="Inspection Photos"
            />
            <div className="flex gap-3">
              <button data-testid="inspection-cancel" onClick={() => setShowInspection(null)} className="flex-1 h-11 border border-slate-200 rounded-md text-sm font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="inspection-submit" onClick={() => handleInspection(showInspection)} disabled={actionLoading === "inspection"}
                className="flex-1 h-11 bg-emerald-600 text-white rounded-md text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
                {actionLoading === "inspection" ? "Submitting..." : "Complete Inspection"}
              </button>
            </div>
          </div>
        )}

        {/* Invoices */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="invoices-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Receipt weight="bold" className="w-4 h-4" /> Invoices
            </h3>
            {["ON_HIRE", "RETURNED", "DAMAGE_REVIEW", "CLOSED", "READY_FOR_PICKUP"].includes(rental.status) && (
              <button data-testid="generate-invoice-btn" onClick={suggestNextPeriod}
                className="flex items-center gap-1.5 h-8 px-3 bg-primary text-primary-foreground rounded-md text-xs font-bold hover:bg-primary/90 transition-colors">
                <Plus weight="bold" className="w-3.5 h-3.5" /> Generate Invoice
              </button>
            )}
          </div>

          {/* New Invoice Form */}
            {showNewInvoice && (
            <div className="border border-black/10 bg-foreground/5 rounded-md p-4 mb-4 space-y-3">
              <h4 className="text-sm font-bold text-slate-900">New Invoice — {getBillingLabel(rental)}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Period Start</label>
                  <input data-testid="invoice-period-start" type="datetime-local" value={invoicePeriod.period_start}
                    onChange={e => setInvoicePeriod(p => ({...p, period_start: e.target.value}))}
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Period End</label>
                  <input data-testid="invoice-period-end" type="datetime-local" value={invoicePeriod.period_end}
                    onChange={e => setInvoicePeriod(p => ({...p, period_end: e.target.value}))}
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 bg-white" />
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="invoice-cancel" onClick={() => setShowNewInvoice(false)}
                  className="h-9 px-4 border border-slate-200 rounded-md text-xs font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button data-testid="invoice-create" onClick={handleCreateInvoice} disabled={actionLoading === "create_invoice"}
                  className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-xs font-bold hover:bg-primary/90 disabled:opacity-50">
                  {actionLoading === "create_invoice" ? "Creating..." : "Create Invoice"}
                </button>
              </div>
            </div>
          )}

          {/* Invoice List */}
          {invoices.length === 0 ? (
            <p className="text-sm text-slate-400">No invoices generated yet</p>
          ) : (
            <div className="space-y-2">
              {invoices.map(inv => (
                <div key={inv.id} data-testid={`invoice-${inv.invoice_number}`}
                  className="flex items-center justify-between p-3 border border-slate-100 rounded-md hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${INVOICE_STATUS_COLORS[inv.status]}15` }}>
                      <CurrencyDollar weight="bold" className="w-4 h-4" style={{ color: INVOICE_STATUS_COLORS[inv.status] }} />
                    </div>
                      <div>
                        <div className="text-sm font-bold text-slate-900">{inv.invoice_number}</div>
                      <div className="text-xs text-slate-500">
                        {inv.period_start?.slice(0,10)} to {inv.period_end?.slice(0,10)} &middot; {inv.units ?? inv.days} {(inv.rate_unit || "day").toLowerCase()}{(inv.units ?? inv.days) === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-black font-mono" style={{ color: INVOICE_STATUS_COLORS[inv.status] }}>${inv.total?.toFixed(2)}</div>
                      <div className="text-[10px] font-bold uppercase" style={{ color: INVOICE_STATUS_COLORS[inv.status] }}>{inv.status}</div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        data-testid={`invoice-pdf-${inv.invoice_number}`}
                        onClick={() => downloadInvoicePdf(inv.id, `Invoice-${inv.invoice_number}.pdf`)}
                        className="w-8 h-8 rounded-md flex items-center justify-center text-red-600 hover:bg-red-50 transition-colors"
                        title="Download PDF"
                      >
                        <FilePdf weight="bold" className="w-4 h-4" />
                      </button>
                      {inv.status === "DRAFT" && inv.customer_email && (
                        <button data-testid={`invoice-send-${inv.invoice_number}`} onClick={() => handleSendInvoice(inv.id)} disabled={actionLoading === `send_${inv.id}`}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-foreground hover:bg-foreground/5 transition-colors" title="Email invoice">
                          <Envelope weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                      {["DRAFT", "SENT"].includes(inv.status) && (
                        <button data-testid={`invoice-paid-${inv.invoice_number}`} onClick={() => handleMarkPaid(inv.id)} disabled={actionLoading === `paid_${inv.id}`}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition-colors" title="Mark as paid">
                          <CheckCircle weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activity */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Activity</h3>
          <div className="flex gap-2 mb-4">
            <input data-testid="activity-note-input" placeholder="Add a note..." className="flex-1 h-10 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10"
              value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()} />
            <button data-testid="activity-note-submit" onClick={addNote} className="h-10 px-4 bg-slate-900 text-white rounded-md text-sm font-bold hover:bg-slate-800">Add</button>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {activity.length === 0 && <p className="text-sm text-slate-400">No activity yet</p>}
            {activity.map(a => (
              <div key={a.id} className="flex gap-3 text-sm">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${a.type === "SYSTEM" ? "bg-slate-100 text-slate-500" : "bg-black/10 text-foreground"}`}>
                  {a.type === "SYSTEM" ? "S" : getUserName(a.author_id)?.charAt(0)}
                </div>
                <div>
                  <div className="text-slate-700">{a.content}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{getUserName(a.author_id)} &middot; {formatDateTime(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
